import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { EventConfig, Locale } from '@/types'
import { BUILTIN_SETTINGS, migrateLegacy, resolveEvent, type SettingsValues } from '../../server/lib/eventSettings'

interface AppState {
  locale: Locale
  setLocale: (locale: Locale) => void
  darkMode: boolean
  setDarkMode: (v: boolean) => void
  sidebarOpen: boolean
  setSidebarOpen: (v: boolean) => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      locale: 'en',
      setLocale: (locale) => set({ locale }),
      darkMode: true,
      setDarkMode: (darkMode) => set({ darkMode }),
      sidebarOpen: true,
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
    }),
    { name: 'posebox-app' }
  )
)

interface EventState {
  events: EventConfig[]
  storedEvents: Record<string, EventConfig>
  defaults: SettingsValues
  defaultsRevision: number
  setDefaults: (defaults: SettingsValues, revision: number) => void
  activeEventId: string | null
  setEvents: (events: EventConfig[]) => void
  addEvent: (event: EventConfig) => void
  updateEvent: (id: string, updates: Partial<EventConfig>) => void
  deleteEvent: (id: string) => void
  renameEventId: (oldId: string, newId: string) => void
  setActiveEventId: (id: string | null) => void
  loadEvents: () => Promise<void>
  saveEvent: (event: EventConfig) => Promise<void>
}

export const useEventStore = create<EventState>((set, get) => ({
  events: [],
  storedEvents: {},
  defaults: structuredClone(BUILTIN_SETTINGS),
  defaultsRevision: 0,
  setDefaults: (defaults, defaultsRevision) => set(s => ({
    defaults, defaultsRevision,
    events: s.events.map(event => resolveEvent(s.storedEvents[event.id] || event, defaults)),
  })),
  activeEventId: null,
  setEvents: events => set(s => ({
    storedEvents: Object.fromEntries(events.map(e => [e.id, migrateLegacy(e)])),
    events: events.map(e => resolveEvent(e, s.defaults)),
  })),
  addEvent: event => set(s => ({
    storedEvents: { ...s.storedEvents, [event.id]: migrateLegacy(event) },
    events: [resolveEvent(event, s.defaults), ...s.events.filter(e => e.id !== event.id)],
  })),
  updateEvent: (id, updates) => set(s => {
    const current = s.storedEvents[id] || s.events.find(e => e.id === id)
    if (!current) return s
    const stored = { ...current, ...updates, updatedAt: new Date().toISOString() }
    return {
      storedEvents: { ...s.storedEvents, [id]: stored },
      events: s.events.map(e => e.id === id ? resolveEvent(stored, s.defaults) : e),
    }
  }),
  deleteEvent: id => set(s => {
    const storedEvents = { ...s.storedEvents }
    delete storedEvents[id]
    return { events: s.events.filter(e => e.id !== id), storedEvents }
  }),
  renameEventId: (oldId, newId) => set(s => {
    const storedEvents = { ...s.storedEvents }
    if (storedEvents[oldId]) storedEvents[newId] = { ...storedEvents[oldId], id: newId }
    delete storedEvents[oldId]
    return { storedEvents, events: s.events.map(e => e.id === oldId ? { ...e, id: newId } : e) }
  }),
  setActiveEventId: activeEventId => set({ activeEventId }),
  loadEvents: async () => {
    const [listResponse, defaultsResponse] = await Promise.all([fetch('/api/events'), fetch('/api/settings/event-defaults')])
    if (!listResponse.ok || !defaultsResponse.ok) throw new Error('Failed to load events and general settings')
    const [list, settings] = await Promise.all([listResponse.json(), defaultsResponse.json()])
    const details = await Promise.all(list.map(async (ev: { id: string }) => {
      const response = await fetch(`/api/events/${ev.id}`)
      if (!response.ok) throw new Error(`Failed to load event ${ev.id}`)
      return response.json() as Promise<{ config: EventConfig; storedConfig: EventConfig }>
    }))
    set({
      defaults: settings.defaults, defaultsRevision: settings.revision,
      storedEvents: Object.fromEntries(details.map(detail => [detail.config.id, detail.storedConfig])),
      // Resolve against the same defaults snapshot even if defaults changed during the list request.
      events: details.map(detail => resolveEvent(detail.storedConfig, settings.defaults)),
    })
  },
  saveEvent: async event => {
    const draftAtStart = get().storedEvents[event.id]
    const exists = !!get().storedEvents[event.id] || get().events.some(e => e.id === event.id)
    const response = await fetch(exists ? `/api/events/${event.id}` : '/api/events', {
      method: exists ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: event.id, name: event.name, config: event }),
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to save event')
    set(s => {
      const latest = s.storedEvents[event.id]
      // Do not replace a newer unsaved draft with an older request's response.
      if (latest && latest !== draftAtStart) return s
      const stored = data.storedConfig as EventConfig
      return {
        storedEvents: { ...s.storedEvents, [event.id]: stored },
        events: exists ? s.events.map(e => e.id === event.id ? resolveEvent(stored, s.defaults) : e) : [resolveEvent(stored, s.defaults), ...s.events],
      }
    })
  },
}))

interface User {
  id: string
  email: string
  name: string
}

interface AuthState {
  user: User | null
  initialized: boolean
  setUser: (user: User | null) => void
  checkAuth: () => Promise<void>
  logout: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  initialized: false,
  setUser: (user) => set({ user, initialized: true }),
  checkAuth: async () => {
    try {
      const res = await fetch('/api/auth/me')
      if (res.ok) {
        const data = await res.json()
        set({ user: data.user, initialized: true })
      } else {
        set({ user: null, initialized: true })
      }
    } catch {
      set({ user: null, initialized: true })
    }
  },
  logout: async () => {
    await fetch('/api/auth/logout', { method: 'POST' })
    set({ user: null })
  }
}))
